/**
 * planPresentation.ts — Presentation-only plan configuration
 *
 * This file is the DISPLAY source of truth for plan names, features, and copy.
 * It does NOT control entitlement enforcement — that remains in backend capabilities.py.
 *
 * Usage: Import from here for any pricing/upgrade UI that needs plan metadata.
 */

// ---------------------------------------------------------------------------
// Canonical plan keys — match backend plan_tier values
// ---------------------------------------------------------------------------
export type PlanKey = 'free' | 'verified' | 'pro';

// ---------------------------------------------------------------------------
// Legacy display name → canonical display name mapping
// Existing code uses many variants. This maps them all to one canonical label.
// Do NOT use this to change backend tier semantics.
// ---------------------------------------------------------------------------
export const LEGACY_NAME_MAP: Record<string, string> = {
  'Free Member': 'Free',
  'Free Verified': 'Verified',
  'Pro Member': 'Pro',
  'Pro Trial': 'Pro (Trial)',
  'Pro Verified': 'Pro + Verified',
  'premium': 'Pro',
  'free': 'Free',
  'verified': 'Verified',
};

// ---------------------------------------------------------------------------
// Feature line items for plan comparison
// ---------------------------------------------------------------------------
export interface PlanFeature {
  label: string;
  free: boolean | string;
  verified: boolean | string;
  pro: boolean | string;
}

export const PLAN_FEATURES: PlanFeature[] = [
  { label: 'Literature Queues',            free: '1',         verified: '3',         pro: '5' },
  { label: 'Personalized specialty queues', free: true,        verified: true,        pro: true },
  { label: 'LitScreen article screening',  free: true,        verified: true,        pro: true },
  { label: 'LitHub personal library',      free: true,        verified: true,        pro: true },
  { label: 'Reading progress tracking',    free: true,        verified: true,        pro: true },
  { label: 'Run Queue Now',               free: '1/day',     verified: '1/day',     pro: 'Unlimited' },
  { label: 'LitClub community',          free: 'Read only', verified: 'Full access', pro: 'Full access' },
  { label: 'LitStream audio summaries',   free: false,       verified: false,       pro: true },
  { label: 'LitScholar AI deep-dive',     free: false,       verified: false,       pro: true },
  { label: 'Library export (CSV & RIS)',   free: false,       verified: false,       pro: true },
  { label: 'Priority email delivery',     free: false,       verified: false,       pro: true },
];

// ---------------------------------------------------------------------------
// Plan cards — display metadata for the pricing page
// ---------------------------------------------------------------------------
export interface PlanCard {
  key: PlanKey;
  name: string;
  subtitle: string;
  price: string;
  priceNote: string;
  badge?: string;
  highlight: boolean;
  cta: { label: string; href: string };
  gradient: string;
  borderColor: string;
  accentColor: string;
}

export const PLAN_CARDS: PlanCard[] = [
  {
    key: 'free',
    name: 'Free',
    subtitle: 'Get started',
    price: '$0',
    priceNote: 'Free forever',
    highlight: false,
    cta: { label: 'Get Started', href: '/signup' },
    gradient: 'from-slate-50 to-slate-100',
    borderColor: 'border-slate-200',
    accentColor: 'text-slate-700',
  },
  {
    key: 'verified',
    name: 'Verified',
    subtitle: 'For credentialed clinicians',
    price: '$0',
    priceNote: 'Free — verify your credentials',
    badge: 'Most trusted',
    highlight: false,
    cta: { label: 'Verify Credentials', href: '/verification' },
    gradient: 'from-emerald-50 to-teal-50',
    borderColor: 'border-emerald-300',
    accentColor: 'text-emerald-700',
  },
  {
    key: 'pro',
    name: 'Pro',
    subtitle: 'Full access to everything',
    price: 'Coming soon',
    priceNote: '30-day free trial available',
    badge: 'Most popular',
    highlight: true,
    cta: { label: 'Start Free Trial', href: '/plan' },
    gradient: 'from-sky-50 to-blue-50',
    borderColor: 'border-sky-300',
    accentColor: 'text-sky-700',
  },
];

// ---------------------------------------------------------------------------
// Utility: resolve a legacy display name to canonical
// ---------------------------------------------------------------------------
export function canonicalPlanName(legacyName: string): string {
  return LEGACY_NAME_MAP[legacyName] || legacyName;
}
